// Content for the Crestmont University degree-program pages.
//
// Each entry drives one page under /universities/crestmont-university/<slug>.
// The two programs share an identical structure so the page component stays
// content-driven — adding a third program (e.g. an IT bachelor's) is one
// entry here plus a route.

export interface CrestmontStat {
  value: string
  label: string
}

export interface CrestmontCourse {
  code: string
  title: string
  credits: number
}

export interface CurriculumGroup {
  /** Number shown in the group's badge — course count for the group. */
  count: number
  name: string
  description: string
  /** 'navy' | 'gold' | 'ink' — matches the three-panel treatment in the brand guide. */
  tone: 'navy' | 'gold' | 'ink'
  courses: CrestmontCourse[]
}

export interface TermRow {
  term: string
  courses: number
  credits: number
}

export interface Specialization {
  id: string
  name: string
  summary: string
  coreCourses: string[]
  careerOutcomes: string[]
  note?: string
}

export interface DifferentiatorTab {
  id: string
  label: string
  heading: string
  bullets: string[]
}

export interface RequirementTab {
  id: string
  label: string
  items: string[]
}

export interface FaqEntry {
  question: string
  answer: string
}

export interface BenefitCard {
  icon: 'clock' | 'users' | 'shield' | 'globe'
  eyebrow: string
  title: string
}

export interface CrestmontProgram {
  slug: string
  /** Short code used in eyebrows and labels, e.g. "BBA". */
  code: string
  name: string
  shortName: string
  heroSubhead: string
  /** mProgramValue posted to the advertiser for leads from this page. */
  mProgramValue: string
  degreeLevel: string
  positioningEyebrow: string
  positioningHeading: string
  positioningBody: string
  stats: CrestmontStat[]
  positioningBullets: string[]
  testimonial: {
    eyebrow: string
    heading: string
    subheading: string
    quote: string
    name: string
    title: string
    detail: string
    imageUrl: string
  }
  curriculum: {
    heading: string
    subheading: string
    startLabel: string
    startCaption: string
    durationLabel: string
    durationCaption: string
    groups: CurriculumGroup[]
    terms: TermRow[]
    totalCourses: number
    totalCredits: number
  }
  specializations: Specialization[]
  ctaBand: {
    heading: string
    body: string
  }
  differentiators: {
    heading: string
    subheading: string
    tabs: DifferentiatorTab[]
    imageUrl: string
    imageQuote: string
    imageAttribution: string
  }
  requirements: {
    tabs: RequirementTab[]
    tuition: {
      perCredit: string
      credits: number
      feesLabel: string
      feesValue: string
      total: string
    }
  }
  benefits: BenefitCard[]
  faq: FaqEntry[]
}

const SHARED_BENEFITS: BenefitCard[] = [
  {
    icon: 'clock',
    eyebrow: 'Start when you are ready',
    title: 'Applications reviewed year-round',
  },
  {
    icon: 'users',
    eyebrow: 'Taught by practitioners',
    title: 'Faculty who work in the field they teach',
  },
  {
    icon: 'shield',
    eyebrow: 'Independently reviewed',
    title: 'Accredited and transparent',
  },
  {
    icon: 'globe',
    eyebrow: 'No commute, no cohort',
    title: 'Your coursework, wherever you are',
  },
]

const SHARED_FAQ: FaqEntry[] = [
  {
    question: 'How long does the program take to complete?',
    answer:
      'Most students study one course at a time in an accelerated five-week format. Your pace determines your finish date — students transferring approved credit or documented work experience routinely finish sooner than the standard plan.',
  },
  {
    question: 'Do I need to submit test scores to apply?',
    answer:
      'No. Crestmont does not require SAT, ACT, GRE, or GMAT scores for admission to its undergraduate programs.',
  },
  {
    question: 'Can I transfer credit I already earned?',
    answer:
      'Yes. Crestmont evaluates transcripts from accredited institutions and also awards credit for documented work and military training through Prior Learning Assessment. A transfer evaluation is free and happens before you commit.',
  },
  {
    question: 'What does the three-week trial period include?',
    answer:
      'New degree-seeking students can begin their first course and use the online classroom for three weeks before deciding whether to continue. It is designed so you can judge the format on your own experience rather than a brochure.',
  },
  {
    question: 'Is financial aid available?',
    answer:
      'Crestmont participates in federal financial aid programs, and military students or those with employer benefits may qualify for reduced rates or fee waivers. An advisor will walk you through what you are eligible for before you enroll.',
  },
]

export const crestmontPrograms: Record<string, CrestmontProgram> = {
  'bba-programs': {
    slug: 'bba-programs',
    code: 'BBA',
    name: 'Bachelor of Business Administration',
    shortName: 'BBA',
    heroSubhead:
      'For working professionals who are ready to move up, not step away.',
    mProgramValue: 'Bachelor of Business Administration',
    degreeLevel: "Bachelor's",
    positioningEyebrow: 'BBA',
    positioningHeading: 'Built for working professionals ready to advance.',
    positioningBody:
      'The Crestmont BBA turns the judgment you have already built at work into a credential that hiring committees can verify. You study one course at a time, so the degree fits into the week you already have rather than replacing it.',
    stats: [
      { value: '5 yrs', label: 'Standard plan' },
      { value: '$60,705', label: 'Median pay, business roles' },
      { value: '100%', label: 'Online coursework' },
    ],
    positioningBullets: [
      'Take one five-week course at a time — no juggling four syllabi at once',
      'Transfer up to 90 approved credits toward your 120-credit degree',
      'Earn credit for documented work experience through Prior Learning Assessment',
      'No standardized test scores required to apply',
    ],
    testimonial: {
      eyebrow: 'Student Outcomes',
      heading: 'Success Stories From Leaders Like You',
      subheading:
        'Crestmont students are already working. They come here to make what they know count on paper.',
      quote:
        'I had fifteen years of operations experience and no degree to show for it. The BBA let me put a credential behind decisions I was already making, and I moved into a director role eight months before I finished.',
      name: 'Marianne Cole',
      title: 'Director of Operations',
      detail: 'BBA, Class of 2023',
      imageUrl:
        'https://cdn.magicpatterns.com/patterns/generated-images/c6d9c4b0-bc75-4249-ae61-1d35a644d544.jpg',
    },
    curriculum: {
      heading: 'Your BBA Degree',
      subheading: 'One course at a time, from first term to conferral.',
      startLabel: 'IMMEDIATE',
      startCaption: 'New sections open every five weeks',
      durationLabel: '5 YEARS',
      durationCaption: 'Standard plan — faster with transfer credit',
      groups: [
        {
          count: 14,
          name: 'General Education',
          description:
            'The written, quantitative, and analytical foundation every Crestmont bachelor’s degree is built on.',
          tone: 'navy',
          courses: [
            { code: 'ENG 101', title: 'Composition and Rhetoric', credits: 3 },
            { code: 'ENG 210', title: 'Professional Writing', credits: 3 },
            { code: 'MAT 120', title: 'College Algebra', credits: 3 },
            { code: 'MAT 230', title: 'Applied Statistics', credits: 3 },
            { code: 'COM 150', title: 'Interpersonal Communication', credits: 3 },
            { code: 'PSY 101', title: 'Introduction to Psychology', credits: 3 },
            { code: 'SOC 110', title: 'Society and Institutions', credits: 3 },
            { code: 'HIS 140', title: 'Modern World History', credits: 3 },
            { code: 'PHI 205', title: 'Critical Reasoning', credits: 3 },
            { code: 'ETH 250', title: 'Ethics in the Professions', credits: 3 },
            { code: 'SCI 180', title: 'Environmental Science', credits: 3 },
            { code: 'HUM 220', title: 'Arts and Culture', credits: 3 },
            { code: 'IST 130', title: 'Information Literacy', credits: 3 },
            { code: 'GEN 299', title: 'General Education Capstone', credits: 3 },
          ],
        },
        {
          count: 13,
          name: 'Business Core',
          description:
            'The functional grounding every business leader is expected to have — finance through operations.',
          tone: 'gold',
          courses: [
            { code: 'ACC 201', title: 'Financial Accounting', credits: 3 },
            { code: 'ACC 202', title: 'Managerial Accounting', credits: 3 },
            { code: 'ECO 210', title: 'Microeconomics', credits: 3 },
            { code: 'ECO 211', title: 'Macroeconomics', credits: 3 },
            { code: 'FIN 310', title: 'Corporate Finance', credits: 3 },
            { code: 'MGT 220', title: 'Principles of Management', credits: 3 },
            { code: 'MGT 340', title: 'Organizational Behavior', credits: 3 },
            { code: 'MKT 230', title: 'Principles of Marketing', credits: 3 },
            { code: 'BUS 260', title: 'Business Law', credits: 3 },
            { code: 'OPS 330', title: 'Operations Management', credits: 3 },
            { code: 'BUS 350', title: 'Business Analytics', credits: 3 },
            { code: 'MGT 410', title: 'Strategic Management', credits: 3 },
            { code: 'BUS 499', title: 'Senior Capstone', credits: 3 },
          ],
        },
        {
          count: 13,
          name: 'Specialization Electives',
          description:
            'Thirteen courses in the concentration you choose, from healthcare administration to project management.',
          tone: 'ink',
          courses: [
            { code: 'SPC 300', title: 'Specialization Foundations', credits: 3 },
            { code: 'SPC 310', title: 'Applied Methods I', credits: 3 },
            { code: 'SPC 320', title: 'Applied Methods II', credits: 3 },
            { code: 'SPC 330', title: 'Regulatory Environment', credits: 3 },
            { code: 'SPC 340', title: 'Data and Decision Making', credits: 3 },
            { code: 'SPC 350', title: 'Systems and Process Design', credits: 3 },
            { code: 'SPC 360', title: 'Leading Teams in Context', credits: 3 },
            { code: 'SPC 370', title: 'Budgeting and Resources', credits: 3 },
            { code: 'SPC 380', title: 'Risk and Compliance', credits: 3 },
            { code: 'SPC 400', title: 'Stakeholder Communication', credits: 3 },
            { code: 'SPC 410', title: 'Change and Implementation', credits: 3 },
            { code: 'SPC 420', title: 'Field Problem Analysis', credits: 3 },
            { code: 'SPC 430', title: 'Specialization Capstone', credits: 3 },
          ],
        },
      ],
      terms: [
        { term: 'Year 1', courses: 8, credits: 24 },
        { term: 'Year 2', courses: 8, credits: 24 },
        { term: 'Year 3', courses: 8, credits: 24 },
        { term: 'Year 4', courses: 8, credits: 24 },
        { term: 'Year 5', courses: 8, credits: 24 },
      ],
      totalCourses: 40,
      totalCredits: 120,
    },
    specializations: [
      {
        id: 'healthcare-administration',
        name: 'Healthcare Administration',
        summary:
          'For people already working in clinics, practices, or payer operations who want to run them. Covers reimbursement, compliance, and the operating realities of care delivery.',
        coreCourses: [
          'HCA 310 — Health Systems and Policy',
          'HCA 325 — Reimbursement and Revenue Cycle',
          'HCA 340 — Healthcare Compliance and HIPAA',
          'HCA 380 — Quality and Patient Safety',
          'HCA 430 — Healthcare Operations Capstone',
        ],
        careerOutcomes: [
          'Practice manager',
          'Revenue cycle supervisor',
          'Clinical operations manager',
          'Compliance analyst',
        ],
        note: 'Administrative roles generally do not require clinical licensure, though some employers run background checks.',
      },
      {
        id: 'project-management',
        name: 'Project Management',
        summary:
          'Built around the vocabulary and artifacts hiring managers expect — scope, schedule, risk registers, and stakeholder reporting — using work you can pull from your own job.',
        coreCourses: [
          'PMG 310 — Project Scope and Schedule',
          'PMG 325 — Cost and Procurement',
          'PMG 340 — Risk and Quality Management',
          'PMG 380 — Agile and Hybrid Delivery',
          'PMG 430 — Project Management Capstone',
        ],
        careerOutcomes: [
          'Project manager',
          'Program coordinator',
          'Delivery lead',
          'PMO analyst',
        ],
        note: 'Coursework maps to the knowledge areas assessed by common project management certifications.',
      },
      {
        id: 'accounting-finance',
        name: 'Accounting & Finance',
        summary:
          'The reporting and analysis track. Aimed at people who already touch the books and want the credential to move into an analyst or controller path.',
        coreCourses: [
          'ACC 320 — Intermediate Accounting',
          'ACC 350 — Auditing Principles',
          'FIN 340 — Financial Analysis and Valuation',
          'ACC 410 — Federal Taxation',
          'FIN 430 — Finance Capstone',
        ],
        careerOutcomes: [
          'Staff accountant',
          'Financial analyst',
          'Accounting supervisor',
          'Budget analyst',
        ],
        note: 'CPA licensure has separate credit-hour and examination requirements that vary by state.',
      },
    ],
    ctaBand: {
      heading: 'Validate the business skills you already use.',
      body: 'Talk to an advisor about transfer credit before you apply — most BBA students bring some with them.',
    },
    differentiators: {
      heading: 'Unique Online BBA',
      subheading:
        'What actually differs here, in the terms that matter when you are paying for it yourself.',
      tabs: [
        {
          id: 'experience',
          label: 'Work experience counts',
          heading: 'From workplace experience to academic credential',
          bullets: [
            'Prior Learning Assessment converts documented work into credit toward the 120 required',
            'Up to 90 transfer credits accepted from accredited institutions',
            'Military training and industry certifications reviewed for credit',
            'A free transfer evaluation happens before you enroll, not after',
          ],
        },
        {
          id: 'format',
          label: 'One course at a time',
          heading: 'A format that survives a full-time job',
          bullets: [
            'Five-week terms with a single course in each — one syllabus, one deadline set',
            'The online classroom is open 24/7, with no required live sessions',
            'New sections start every five weeks, so a pause costs weeks, not a semester',
            'Coursework is asynchronous and works across time zones and shift schedules',
          ],
        },
        {
          id: 'faculty',
          label: 'Practitioner faculty',
          heading: 'Taught by people doing the work',
          bullets: [
            'Faculty are hired for field experience in the subject they teach',
            'Assignments are built around problems you can source from your own employer',
            'An Honors Program is available for high-achieving bachelor’s students',
            'Advisors stay with you across the program rather than rotating each term',
          ],
        },
        {
          id: 'cost',
          label: 'Predictable cost',
          heading: 'A tuition number you can plan around',
          bullets: [
            'Flat per-credit tuition — the rate does not change mid-program',
            'A three-week trial period before you commit financially',
            'Federal financial aid, employer benefits, and military rates all apply',
            'No fees for the transfer credit evaluation',
          ],
        },
      ],
      imageUrl:
        'https://cdn.magicpatterns.com/patterns/generated-images/c6d9c4b0-bc75-4249-ae61-1d35a644d544.jpg',
      imageQuote:
        'I stopped apologizing for not having a degree. Now the résumé matches the work.',
      imageAttribution: 'Marianne Cole, BBA ’23',
    },
    requirements: {
      tabs: [
        {
          id: 'admissions',
          label: 'Admissions',
          items: [
            'A high school diploma or recognized equivalent',
            'A completed application — no application fee',
            'Official transcripts from any institutions previously attended',
            'A short conversation with an admissions advisor about your goals and transfer credit',
            'No SAT, ACT, GRE, or GMAT scores required',
            'Government-issued identification for enrollment verification',
          ],
        },
        {
          id: 'transfer',
          label: 'Transfer credit',
          items: [
            'Up to 90 of the 120 required credits may be transferred',
            'Credit is accepted from institutionally accredited colleges and universities',
            'Prior Learning Assessment can convert documented work experience into credit',
            'Military transcripts (JST) and approved industry certifications are reviewed',
            'Transfer evaluations are free and completed before you enroll',
            'A minimum of 30 credits must be earned at Crestmont',
          ],
        },
      ],
      tuition: {
        perCredit: '$127',
        credits: 120,
        feesLabel: 'Books & course fees (est.)',
        feesValue: '$960',
        total: '$15,240',
      },
    },
    benefits: SHARED_BENEFITS,
    faq: SHARED_FAQ,
  },
  'aba-programs': {
    slug: 'aba-programs',
    code: 'AA-BA',
    name: 'Associate of Arts in Business Administration',
    shortName: 'AA-BA',
    heroSubhead:
      'For early-career professionals who want a credential in hand sooner.',
    mProgramValue: 'Associate of Arts in Business Administration',
    degreeLevel: "Associate's",
    positioningEyebrow: 'AA-BA',
    positioningHeading: 'Built for early-career professionals ready to grow.',
    positioningBody:
      'The associate degree is the shortest credible path into business roles that ask for a degree — and every credit transfers into the Crestmont BBA if you decide to keep going.',
    stats: [
      { value: '2.5 yrs', label: 'Standard plan' },
      { value: '$53,525', label: 'Median pay, support roles' },
      { value: '100%', label: 'Online coursework' },
    ],
    positioningBullets: [
      'Sixty credits — roughly half the time of a bachelor’s degree',
      'Every credit applies toward the Crestmont BBA if you continue',
      'One five-week course at a time, with no required live sessions',
      'No standardized test scores required to apply',
    ],
    testimonial: {
      eyebrow: 'Student Outcomes',
      heading: 'Success Stories From Leaders Like You',
      subheading:
        'Crestmont students start where they are. The associate degree is often the first credential in the family.',
      quote:
        'I needed something I could actually finish while working nights. Two and a half years later I had the associate, moved into a coordinator role, and rolled every credit straight into the bachelor’s.',
      name: 'Devon Ellis',
      title: 'Business Operations Coordinator',
      detail: 'AA-BA, Class of 2024',
      imageUrl:
        'https://cdn.magicpatterns.com/patterns/generated-images/52a85423-fdb1-430d-b49b-d918acdb6066.jpg',
    },
    curriculum: {
      heading: 'Your AA-BA Degree',
      subheading: 'Twenty courses, one at a time, to a transferable associate.',
      startLabel: 'IMMEDIATE',
      startCaption: 'New sections open every five weeks',
      durationLabel: '2.5 YEARS',
      durationCaption: 'Standard plan — faster with transfer credit',
      groups: [
        {
          count: 10,
          name: 'General Education',
          description:
            'The written, quantitative, and analytical core that transfers into any Crestmont bachelor’s program.',
          tone: 'navy',
          courses: [
            { code: 'ENG 101', title: 'Composition and Rhetoric', credits: 3 },
            { code: 'ENG 210', title: 'Professional Writing', credits: 3 },
            { code: 'MAT 120', title: 'College Algebra', credits: 3 },
            { code: 'MAT 230', title: 'Applied Statistics', credits: 3 },
            { code: 'COM 150', title: 'Interpersonal Communication', credits: 3 },
            { code: 'PSY 101', title: 'Introduction to Psychology', credits: 3 },
            { code: 'SOC 110', title: 'Society and Institutions', credits: 3 },
            { code: 'PHI 205', title: 'Critical Reasoning', credits: 3 },
            { code: 'IST 130', title: 'Information Literacy', credits: 3 },
            { code: 'ETH 250', title: 'Ethics in the Professions', credits: 3 },
          ],
        },
        {
          count: 6,
          name: 'General Elective Courses',
          description:
            'Foundational business coursework that carries directly into the BBA business core.',
          tone: 'gold',
          courses: [
            { code: 'ACC 201', title: 'Financial Accounting', credits: 3 },
            { code: 'ECO 210', title: 'Microeconomics', credits: 3 },
            { code: 'MGT 220', title: 'Principles of Management', credits: 3 },
            { code: 'MKT 230', title: 'Principles of Marketing', credits: 3 },
            { code: 'BUS 260', title: 'Business Law', credits: 3 },
            { code: 'BUS 240', title: 'Business Communication', credits: 3 },
          ],
        },
        {
          count: 4,
          name: 'Specialization Electives',
          description:
            'Four courses in the track you choose — business or information technology.',
          tone: 'ink',
          courses: [
            { code: 'SPC 210', title: 'Specialization Foundations', credits: 3 },
            { code: 'SPC 220', title: 'Applied Practice', credits: 3 },
            { code: 'SPC 230', title: 'Tools and Systems', credits: 3 },
            { code: 'SPC 240', title: 'Specialization Capstone', credits: 3 },
          ],
        },
      ],
      terms: [
        { term: 'Year 1', courses: 8, credits: 24 },
        { term: 'Year 2', courses: 8, credits: 24 },
        { term: 'Year 3 (half)', courses: 4, credits: 12 },
      ],
      totalCourses: 20,
      totalCredits: 60,
    },
    specializations: [
      {
        id: 'business-administration',
        name: 'Business Administration',
        summary:
          'The general track. Broad coverage of accounting, marketing, and management for support and coordinator roles across any industry.',
        coreCourses: [
          'BUS 210 — Small Business Operations',
          'MGT 225 — Supervisory Practice',
          'MKT 235 — Customer and Market Research',
          'BUS 240 — Business Communication',
        ],
        careerOutcomes: [
          'Business operations coordinator',
          'Office manager',
          'Accounts payable specialist',
          'Sales support associate',
        ],
      },
      {
        id: 'information-technology',
        name: 'Information Technology',
        summary:
          'For people moving into technical support and systems roles. Covers hardware, networking fundamentals, and the help-desk workflows employers actually run.',
        coreCourses: [
          'IST 210 — Computing Fundamentals',
          'IST 220 — Networking Essentials',
          'IST 230 — Operating Systems Support',
          'IST 240 — IT Support Capstone',
        ],
        careerOutcomes: [
          'IT support specialist',
          'Help desk technician',
          'Junior systems administrator',
          'Technical operations associate',
        ],
        note: 'Coursework aligns with entry-level industry certifications, which are earned separately.',
      },
    ],
    ctaBand: {
      heading: 'Build core skills, and keep the door open.',
      body: 'Every AA-BA credit transfers into the Crestmont BBA — the associate is a finish line or a first step.',
    },
    differentiators: {
      heading: 'Unique Online AA-BA',
      subheading:
        'Why an associate here is worth more than the two years it takes.',
      tabs: [
        {
          id: 'stackable',
          label: 'A credential you can build on',
          heading: 'A credential you can build on',
          bullets: [
            'All 60 credits apply toward the Crestmont BBA with no re-application',
            'General education transfers to other accredited institutions as well',
            'You can stop at the associate without losing progress toward a bachelor’s',
            'Advisors map the full path to 120 credits before you start',
          ],
        },
        {
          id: 'format',
          label: 'One course at a time',
          heading: 'A format that survives a full-time job',
          bullets: [
            'Five-week terms with a single course in each',
            'The online classroom is open 24/7, with no required live sessions',
            'New sections start every five weeks',
            'Fully asynchronous — workable on nights and rotating shifts',
          ],
        },
        {
          id: 'entry',
          label: 'Straightforward entry',
          heading: 'Designed for a first attempt at college',
          bullets: [
            'A high school diploma or equivalent is the only academic prerequisite',
            'No standardized test scores and no application fee',
            'A three-week trial period before you commit financially',
            'Credit available for documented work experience through Prior Learning Assessment',
          ],
        },
        {
          id: 'cost',
          label: 'Predictable cost',
          heading: 'A tuition number you can plan around',
          bullets: [
            'Flat per-credit tuition that does not change mid-program',
            'Half the credits of a bachelor’s degree, at the same rate',
            'Federal financial aid, employer benefits, and military rates all apply',
            'No fees for the transfer credit evaluation',
          ],
        },
      ],
      imageUrl:
        'https://cdn.magicpatterns.com/patterns/generated-images/52a85423-fdb1-430d-b49b-d918acdb6066.jpg',
      imageQuote:
        'It was the first degree in my family. Two and a half years, one course at a time.',
      imageAttribution: 'Devon Ellis, AA-BA ’24',
    },
    requirements: {
      tabs: [
        {
          id: 'admissions',
          label: 'Admissions',
          items: [
            'A high school diploma or recognized equivalent',
            'A completed application — no application fee',
            'Official transcripts from any institutions previously attended',
            'A short conversation with an admissions advisor about your goals',
            'No SAT or ACT scores required',
            'Government-issued identification for enrollment verification',
          ],
        },
        {
          id: 'transfer',
          label: 'Transfer credit',
          items: [
            'Up to 45 of the 60 required credits may be transferred',
            'Credit is accepted from institutionally accredited colleges and universities',
            'Prior Learning Assessment can convert documented work experience into credit',
            'Military transcripts (JST) and approved certifications are reviewed',
            'Transfer evaluations are free and completed before you enroll',
            'A minimum of 15 credits must be earned at Crestmont',
          ],
        },
      ],
      tuition: {
        perCredit: '$127',
        credits: 60,
        feesLabel: 'Books & course fees (est.)',
        feesValue: '$480',
        total: '$7,620',
      },
    },
    benefits: SHARED_BENEFITS,
    faq: SHARED_FAQ,
  },
}

export const CRESTMONT_ACCREDITORS: { abbr: string; name: string }[] = [
  { abbr: 'DEAC', name: 'Distance Education Accrediting Commission' },
  { abbr: 'CHEA', name: 'Council for Higher Education Accreditation' },
  { abbr: 'BPPE', name: 'Bureau for Private Postsecondary Education' },
  { abbr: 'ACBSP', name: 'Accreditation Council for Business Schools' },
  { abbr: 'NC-SARA', name: 'National Council for State Authorization' },
]

export function getCrestmontProgram(slug?: string): CrestmontProgram | undefined {
  if (!slug) return undefined
  return crestmontPrograms[slug.toLowerCase()]
}
